import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { graphqlClient } from "@/lib/graphql";
import { useAuth } from "@/hooks/useAuth";
import type { ShopListing } from "@/types";

const SET_PRICE_DROP_ALERT_MUTATION = gql`
  mutation SetPriceDropAlert($gameId: ID!, $targetPrice: Float, $enabled: Boolean!) {
    setPriceDropAlert(gameId: $gameId, targetPrice: $targetPrice, enabled: $enabled) {
      wishlist {
        id
        maxPrice
        notifyOnPriceDrop
      }
    }
  }
`;

interface Props {
  gameId: string;
  shopListings: ShopListing[];
  currentTargetPrice?: number | null;
  alertEnabled: boolean;
}

function suggestDefaultPrice(shopListings: ShopListing[]): number | null {
  const prices = shopListings
    .map((s) => s.price)
    .filter((p): p is number => p != null);
  if (prices.length === 0) return null;
  const cheapest = Math.min(...prices);
  return Math.max(1, Math.floor(cheapest * 0.9));
}

export function PriceDropAlertButton({ gameId, shopListings, currentTargetPrice, alertEnabled }: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [targetPrice, setTargetPrice] = useState<string>(
    currentTargetPrice != null ? String(currentTargetPrice) : ""
  );
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (open && !targetPrice) {
      const suggested = suggestDefaultPrice(shopListings);
      if (suggested != null) setTargetPrice(String(suggested));
    }
  }, [open, shopListings, targetPrice]);

  const setAlert = useMutation({
    mutationFn: (vars: { enabled: boolean; targetPrice?: number }) =>
      graphqlClient.request(SET_PRICE_DROP_ALERT_MUTATION, {
        gameId,
        enabled: vars.enabled,
        targetPrice: vars.targetPrice ?? null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["game", gameId] });
      queryClient.invalidateQueries({ queryKey: ["tracker"] });
      setOpen(false);
    },
  });

  const handleClick = () => {
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    setOpen((v) => !v);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const num = parseFloat(targetPrice);
    if (Number.isNaN(num) || num <= 0) return;
    setAlert.mutate({ enabled: true, targetPrice: num });
  };

  const handleDisable = () => {
    setAlert.mutate({ enabled: false });
  };

  const buttonLabel = alertEnabled
    ? t("notify_me.button_enabled", { price: currentTargetPrice?.toFixed(0) ?? "" })
    : t("notify_me.button_disabled");

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleClick}
        className={`flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
          alertEnabled
            ? "bg-amber-100 text-amber-900 hover:bg-amber-200"
            : "bg-amber-50 text-amber-800 hover:bg-amber-100"
        }`}
      >
        <svg className="h-5 w-5" fill={alertEnabled ? "currentColor" : "none"} viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {buttonLabel}
      </button>

      {open && user && (
        <div
          ref={popoverRef}
          className="absolute left-0 right-0 top-full z-30 mt-2 rounded-xl border border-gray-200 bg-white p-4 shadow-lg"
        >
          <p className="text-sm font-semibold text-gray-900">
            {t("notify_me.popover_title")}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {t("notify_me.popover_description")}
          </p>
          <form onSubmit={handleSubmit} className="mt-3 flex items-stretch gap-2">
            <div className="relative flex-1">
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={targetPrice}
                onChange={(e) => setTargetPrice(e.target.value)}
                placeholder="30"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-8 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                autoFocus
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                €
              </span>
            </div>
            <button
              type="submit"
              disabled={setAlert.isPending || !targetPrice}
              className="rounded-lg bg-primary-800 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
            >
              {alertEnabled ? t("notify_me.submit_update") : t("notify_me.submit_create")}
            </button>
          </form>
          {alertEnabled && (
            <button
              type="button"
              onClick={handleDisable}
              disabled={setAlert.isPending}
              className="mt-2 w-full text-xs text-gray-500 hover:text-red-600"
            >
              {t("notify_me.disable_link")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
